import type { PlayKind, SlotTrailState, TrailSquare } from '@goodtime/shared';

/**
 * The rules of the Sweet Trail's panel on slots without the road (producer
 * 2026-10-06: "it only needs to show when progress is made"): what a new
 * trail state from a spin does on screen, and where the panel goes. The
 * panel ({@link ../../widgets/trail/TrailPanel}) draws them.
 */

/** How long the board stays out after a hop (when not pinned open). */
export const TRAIL_SHOW_MS = 3200;
/** How long the board is up before the road rolls after a hop. */
export const TRAIL_ROLL_AFTER_MS = 380;
/** How long a hop's prize plate shows. */
export const TRAIL_PRIZE_MS = 2400;

/** A trail square as the road strip's label (`coins:50`, `run`, ...). */
export function trailSquareLabel(square: TrailSquare): string {
  switch (square.kind) {
    case 'COINS':
      return `coins:${square.amount}`;
    case 'GEMS':
      return `gems:${square.amount}`;
    case 'FREE_SPINS':
      return `fs:${square.amount}`;
    case 'BONUS':
      return 'run';
    default:
      return 'start';
  }
}

/** The coins a spin's hops paid on the trail (already in the spin's
 *  wallet: they fly in from the trail once it shows them). */
export function trailStepCoins(trail: Pick<SlotTrailState, 'steps'> | null | undefined): number {
  return trail?.steps.reduce((sum, step) => sum + (step.kind === 'COINS' ? Math.max(0, step.amount) : 0), 0) ?? 0;
}

/** The pick-a-prize bonus is waiting to be played. */
export function trailBonusOpen(trail: SlotTrailState): boolean {
  return trail.bonus !== null && !trail.bonus.finished;
}

/** What opening the trail's bonus plays (see {@link trailBonusRoute}). */
export type TrailBonusRoute =
  | { kind: 'resume'; playId: string; game: PlayKind }
  | { kind: 'game'; game: PlayKind }
  | { kind: 'boxes' };

/**
 * What opening the trail's bonus plays: the trail's game again when one
 * was left open (a reload, another tab); the game the bonus rolled, while
 * no box has been picked; otherwise the boxes. Null when nothing waits. A
 * server without trail games sends neither field, so it's always the boxes.
 */
export function trailBonusRoute(trail: SlotTrailState): TrailBonusRoute | null {
  if (trail.game) return { kind: 'resume', playId: trail.game.playId, game: trail.game.kind };
  const bonus = trail.bonus;
  if (!bonus) return null;
  if (bonus.game && !bonus.finished && bonus.picks.length === 0) return { kind: 'game', game: bonus.game };
  return { kind: 'boxes' };
}

/** Squares from `position` to the bonus at the end. */
export function trailStepsToBonus(trail: SlotTrailState, position = trail.position): number {
  return Math.max(0, trail.squares.length - 1 - position);
}

/** What a spin's trail state does on screen. */
export interface TrailMoment {
  /** Collector symbols landed (popped over the meter); 0 for none. */
  collected: number;
  /** The token reached at least one square. */
  hop: boolean;
  /** Bring the board out: after a hop, or once when a new trail collects
   *  its first symbols (`intro`, with the line saying how it works). */
  show: { ms: number; intro: boolean } | null;
  /** What the hop paid, as one plate: the last kind paid, summed. */
  prize: TrailSquare | null;
}

/**
 * The moment a spin's trail state makes. A bonus square isn't a prize (the
 * bonus opens instead), and the board stays out on its own while a bonus
 * waits, so a hop onto it shows nothing extra.
 */
export function trailMoment(trail: SlotTrailState): TrailMoment {
  const paid = trail.steps.filter((s) => s.kind !== 'BONUS');
  const last = paid[paid.length - 1];
  const prize = last
    ? { kind: last.kind, amount: paid.filter((s) => s.kind === last.kind).reduce((n, s) => n + s.amount, 0) }
    : null;
  const hop = trail.steps.length > 0;
  let show: TrailMoment['show'] = null;
  if (hop) {
    if (!trailBonusOpen(trail)) show = { ms: TRAIL_SHOW_MS, intro: false };
  } else if (trail.level === 1 && trail.position === 0 && trail.collected > 0 && trail.meter === trail.collected) {
    // A brand-new trail's first collection: introduce it once.
    show = { ms: TRAIL_SHOW_MS + 800, intro: true };
  }
  return { collected: trail.collected, hop, show, prize };
}

/** A prize in words, for the plate after a hop and the bonus's boxes. */
export function trailPrizeText(prize: TrailSquare): string {
  switch (prize.kind) {
    case 'COINS':
      return `+${prize.amount.toLocaleString('en-GB')}`;
    case 'GEMS':
      return `+${prize.amount}`;
    case 'FREE_SPINS':
      return `+${prize.amount} free spins`;
    default:
      return '';
  }
}

/** The line under the board, when there's room for one. */
export function trailHint(trail: SlotTrailState, intro: boolean, collectorNoun: string): string {
  if (trailBonusOpen(trail)) {
    // A game stays a surprise until its title card.
    return trailBonusRoute(trail)?.kind === 'game' ? 'Bonus game ready: tap the gift' : 'Bonus ready: tap the gift';
  }
  if (intro) return `Collect ${trail.stepCost} ${collectorNoun} to move a step`;
  return '';
}

/** What the bonus says above its boxes. */
export function trailBonusLead(trail: SlotTrailState): string {
  const bonus = trail.bonus;
  if (!bonus) return '';
  if (bonus.finished) return 'Here was every box';
  return `Pick ${bonus.picksLeft} ${bonus.picksLeft === 1 ? 'box' : 'boxes'}`;
}

/** The slot's frame and the screen, in canvas (CSS) pixels. */
export interface TrailBounds {
  width: number;
  height: number;
  /** The notch or status bar at the top. */
  safeTop: number;
  machineLeft: number;
  machineRight: number;
  machineTop: number;
  spinButtonY: number;
}

/** Where the trail's chip and board go, and how big the board is. */
export interface TrailLayout {
  x: number;
  y: number;
  width: number;
  /** Smaller pieces for a tight space. */
  compact: boolean;
  /** One row between the top bar's corners (wide screens): no line under it. */
  wide: boolean;
}

/** The board's height: full (with its line), compact, and wide. */
export const TRAIL_BOARD_HEIGHT = { full: 132, compact: 88, wide: 80 } as const;

/**
 * Where the trail goes (producer 2026-10-06: "better at the top, unless it
 * gets in the way"):
 * - wide screens: one slim row, top centre, between the back button and
 *   the coin bar (where the reels reach the top, it can rest on the
 *   frame's top edge, never on the symbols);
 * - phones: in the sky under the coin bar when it clears the reels, else
 *   under the SPIN row, else (short phones) at the top, compact.
 */
export function trailLayout(b: TrailBounds): TrailLayout {
  if (b.width >= 700) {
    const width = Math.min(640, b.width - 440);
    return { x: Math.round(b.width / 2 - width / 2), y: b.safeTop + 6, width, compact: true, wide: true };
  }
  const width = Math.max(240, Math.min(400, b.machineRight - b.machineLeft - 16, b.width - 24));
  const x = Math.round((b.machineLeft + b.machineRight) / 2 - width / 2);
  const y = b.safeTop + 62;
  const room = b.machineTop - y - 6;
  if (room >= TRAIL_BOARD_HEIGHT.full) return { x, y, width, compact: false, wide: false };
  if (room >= TRAIL_BOARD_HEIGHT.compact) return { x, y, width, compact: true, wide: false };
  const below = b.spinButtonY + 44;
  const roomBelow = b.height - 76 - below;
  if (roomBelow >= TRAIL_BOARD_HEIGHT.compact) {
    return { x, y: below, width, compact: roomBelow < TRAIL_BOARD_HEIGHT.full, wide: false };
  }
  // Nowhere fits cleanly: the top, compact. At worst it touches the reel
  // frame's border, which beats covering Play with others.
  return { x, y, width, compact: true, wide: false };
}
